import React, { useEffect, useMemo, useState } from 'react';
import { placementDocsAPI } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { LETTER_PILL } from './CycleBoard';
import PlacementDetailsDialog from './PlacementDetailsDialog';
import { usePagination, PaginationBar } from './PaginationBar';

/**
 * List view of a cycle's placements — the "☰ List" half of the Board's
 * view toggle. Same data as the Board with the same status pills.
 */
export const CyclePlacementsList = ({ cycle }) => {
  const [docRows, setDocRows] = useState([]);
  const [detailsId, setDetailsId] = useState(null);

  useEffect(() => {
    placementDocsAPI.overview().then((r) => setDocRows(r.data || [])).catch(() => {});
  }, [cycle.placements]);

  const docsByPlacement = useMemo(() => {
    const m = {};
    docRows.forEach((r) => { m[r.placement_id] = r; });
    return m;
  }, [docRows]);

  const rows = useMemo(() => {
    const sById = Object.fromEntries((cycle.students || []).map((s) => [s.id, s]));
    const rById = Object.fromEntries((cycle.restaurants || []).map((r) => [r.id, r]));
    return (cycle.placements || [])
      .map((p) => ({ ...p, student: sById[p.student_id], restaurant: rById[p.restaurant_id] }))
      .sort((a, b) => ((a.restaurant?.name || '') + (a.student?.name || '')).localeCompare((b.restaurant?.name || '') + (b.student?.name || '')));
  }, [cycle]);
  const pg = usePagination(rows, 'cycle-list');

  if (rows.length === 0) {
    return <Card><CardContent className="py-8 text-center text-sm text-gray-500">No placements yet for this cycle. Switch to the Board to drag students onto restaurants.</CardContent></Card>;
  }

  return (
    <Card><CardContent className="p-0">
      <PaginationBar {...pg.bar} position="top" noun="placements" />
      <div className="table-scroll">
      <table className="w-full text-sm" data-testid="cycle-placements-list">
        <thead>
          <tr className="bg-gray-50 border-b text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
            <th className="text-left py-2.5 px-4">Student</th>
            <th className="text-left py-2.5 px-4">Restaurant</th>
            <th className="text-left py-2.5 px-4 hidden md:table-cell">Dates</th>
            <th className="text-left py-2.5 px-4">Confirmed</th>
            <th className="text-left py-2.5 px-4">Letter</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {pg.pageItems.map((p) => {
            const confirmed = (p.ack_status || '').toLowerCase() === 'signed' || (p.status || '').toLowerCase() === 'active';
            const letterStatus = (docsByPlacement[p.id]?.documents?.placement_letter?.status || 'not_created').toLowerCase();
            const pill = LETTER_PILL[letterStatus] || LETTER_PILL.not_created;
            return (
              <tr
                key={p.id}
                data-testid={`cycle-list-row-${p.id}`}
                onClick={() => setDetailsId(p.id)}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <td className="py-2.5 px-4">
                  <div className="font-medium text-gray-900">{p.student?.name || p.student_id}</div>
                  <div className="text-[11px] text-gray-500">{p.student?.student_number} · {p.position || 'Placement'}</div>
                </td>
                <td className="py-2.5 px-4 text-ica-navy font-medium">
                  <span className="inline-flex items-center gap-1"><ArrowRight className="w-3 h-3 text-gray-400" />{p.restaurant?.name || p.restaurant_id}</span>
                </td>
                <td className="py-2.5 px-4 text-xs text-gray-600 hidden md:table-cell">{p.start_date} → {p.end_date}</td>
                <td className="py-2.5 px-4">
                  <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${confirmed ? 'text-emerald-600' : 'text-gray-400'}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> {confirmed ? 'Confirmed' : 'Pending'}
                  </span>
                </td>
                <td className="py-2.5 px-4">
                  <span className={`inline-flex items-center gap-1 text-[11px] font-medium rounded px-1.5 py-0.5 ${pill.cls}`}>
                    <pill.Icon className="w-3 h-3" /> {pill.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <PaginationBar {...pg.bar} position="bottom" noun="placements" />
      <PlacementDetailsDialog open={!!detailsId} onOpenChange={(o) => { if (!o) setDetailsId(null); }} placementId={detailsId} />
    </CardContent></Card>
  );
};

export default CyclePlacementsList;
